import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
} from "../../ipd/utils";
import { assignBed, getAssignable } from "../api/emergencyApi";
import { firePendingCharges } from "../api/emergencyBillingApi";
import { truncatedSelectSx } from "../constants";
import { toast } from "react-toastify";
import EmergencyDoctorAutocomplete from "./EmergencyDoctorAutocomplete";

/**
 * Placing a patient in a bed (ER-BED-001/002/003).
 *
 * Bed and Emergency Provider are one action, not two steps, because a bed is
 * never assigned on its own — the Save button stays disabled until both are
 * chosen rather than letting someone submit half of it and find out after.
 *
 * Only free beds in active categories are listed, and the list comes from the
 * server rather than being filtered here: offering a bed the assignment would
 * then refuse is a worse failure than a slightly slower dialog.
 */
export default function AssignBedDialog({ open, visit, onClose, onAssigned }) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bedId, setBedId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setOptions(await getAssignable());
    } catch (e) {
      setError(e?.message || "Could not load the available beds");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setBedId("");
    // Re-assigning keeps the current provider pre-selected — a bed move is
    // usually not a change of doctor.
    setProviderId(visit?.provider_id || "");
    setError("");
    load();
  }, [open, visit, load]);

  const submit = async () => {
    if (!bedId || !providerId) return;
    setSaving(true);
    setError("");
    try {
      const updated = await assignBed(visit.id, { bedId, providerId });
      // The server recorded the bed charge with the assignment; posting it to
      // billing is not awaited — anything that does not post shows in the
      // visit's charge check.
      firePendingCharges(visit.id);
      // The server decides what happened to the vacated bed, since the
      // housekeeping rule lives there — report its answer rather than guessing.
      onAssigned?.(updated);
      onClose();
    } catch (e) {
      // A bed taken by someone else between opening and saving is the expected
      // race here, so refresh the list rather than leaving a stale choice up.
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        "Could not assign the bed";
      setError(msg);
      toast.error(msg);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (!visit) return null;

  const freeBeds = options?.free_beds || [];
  const cleaningBeds = options?.cleaning_beds || [];
  const doctors = options?.doctors || [];
  const noBeds = !loading && freeBeds.length === 0 && cleaningBeds.length === 0;

  const isSelectedBedUnavailable = Boolean(
    bedId &&
      (cleaningBeds.some((b) => b.id === bedId) ||
        freeBeds.some(
          (b) =>
            b.id === bedId &&
            (b.is_available === false ||
              (b.status && !["AVAILABLE", "FREE"].includes(String(b.status).toUpperCase())))
        ))
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          {visit.bed_id ? "Move to another bed" : "Assign bed & provider"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {visit.bed_id
            ? `${visit.bed_label} is released under the hospital's housekeeping rule.`
            : "A bed is never assigned on its own — name the Emergency Provider at the same time."}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
          </Box>
        ) : noBeds ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <HotelIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              No free beds right now
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
              Every emergency bed is occupied or being cleaned. The patient stays on the board
              until a bed is released.
            </Typography>
          </Box>
        ) : (
          /* minmax(0, 1fr) rather than a bare 1fr: a grid item defaults to
             min-width auto and so refuses to shrink below its content, which
             let a long doctor name push the select wider than the dialog and
             scroll the whole thing sideways. */
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "minmax(0, 1fr)" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                Free bed <span style={{ color: IPD_COLORS.error.text }}>*</span>
              </Typography>
              <TextField
                select
                value={bedId}
                onChange={(e) => setBedId(e.target.value)}
                size="small"
                fullWidth
                sx={{ ...ipdElevatedInputSx, ...truncatedSelectSx }}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                inputProps={{ "aria-label": "Free bed" }}
              >
                <MenuItem value="" disabled>Select a free bed</MenuItem>
                {freeBeds.map((b) => {
                  const isUnavailable =
                    b.is_available === false ||
                    (b.status && !["AVAILABLE", "FREE"].includes(String(b.status).toUpperCase()));
                  return (
                    <MenuItem key={b.id} value={b.id} disabled={isUnavailable}>
                      {b.label}
                      {b.category_name ? ` — ${b.category_name}` : ""}
                      {isUnavailable ? " (unavailable)" : ""}
                    </MenuItem>
                  );
                })}
                {cleaningBeds.map((b) => (
                  <MenuItem key={b.id} value={b.id} disabled>
                    {b.label}
                    {b.category_name ? ` — ${b.category_name}` : ""} (being cleaned)
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                Emergency Provider <span style={{ color: IPD_COLORS.error.text }}>*</span>
              </Typography>
              <EmergencyDoctorAutocomplete
                doctors={doctors}
                value={providerId}
                onChange={setProviderId}
                placeholder={doctors.length ? "Search the responsible doctor" : "No doctors at this facility"}
                helperText="Responsible for this visit only — not the admitting doctor if the patient is later admitted."
                ariaLabel="Emergency Provider"
                inDialog
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <IpdButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </IpdButton>
        <IpdButton
          onClick={submit}
          loading={saving}
          disabled={!bedId || !providerId || isSelectedBedUnavailable || noBeds}
        >
          {visit.bed_id ? "Move patient" : "Assign bed"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
